import { useState } from 'react'
import { useAuth } from '@/lib/auth-context'

interface CardData {
  pan: string;
  exp_month: number;
  exp_year: number;
  cvc: string;
  last4: string;
}

interface IssueCardModalProps {
  open: boolean;
  onClose?: () => void;
  merchant?: string;
  programId?: string;
}

export default function IssueCardModal({ open, onClose, merchant, programId = 'sponsor-classroom' }: IssueCardModalProps) {
  const { session } = useAuth()
  const [amount, setAmount] = useState('')
  const [loading, setLoading] = useState(false)
  const [card, setCard] = useState<CardData | null>(null)
  const [error, setError] = useState('')

  if (!open) return null

  const merchantLabel = merchant || 'selected merchant'

  const handleClose = () => {
    setAmount('')
    setLoading(false)
    setError('')
    setCard(null)
    onClose?.()
  }

  const handleIssue = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setError('')
    setCard(null)

    if (!merchant) {
      setError('Select a merchant before issuing a card')
      return
    }

    const cents = Math.round(parseFloat(amount || '0') * 100)
    if (!Number.isFinite(cents) || cents <= 0) {
      setError('Enter a valid amount')
      return
    }

    try {
      setLoading(true)
      const CORE_API = import.meta.env.VITE_CORE_API_URL || ''
      const response = await fetch(`${CORE_API}/api/v1/teacher/cards/issue`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session?.access_token || ''}`,
        },
        body: JSON.stringify({ merchant, amountCents: cents, programId }),
      })

      const raw = await response.json().catch(() => ({}))
      if (!response.ok) {
        const errMsg = typeof raw?.error === 'string' ? raw.error : raw?.error?.message
        throw new Error(errMsg || 'Unable to issue card')
      }

      const data = raw?.data ?? raw
      setCard(data.card)
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Unknown error')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/30 backdrop-blur-sm flex items-center justify-center px-4">
      <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-soft space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-cocoa">Issue one-time card ({merchantLabel})</h3>
          <button
            type="button"
            className="text-cocoa/60 hover:text-cocoa"
            onClick={handleClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {!card ? (
          <form className="space-y-4" onSubmit={handleIssue}>
            <label className="block text-sm text-cocoa/80">
              <span className="font-medium text-cocoa">Cart total (USD)</span>
              <input
                className="mt-2 w-full rounded-xl border border-sand bg-white px-3 py-2 text-base text-cocoa focus:border-brick focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brick focus-visible:ring-2 focus-visible:ring-brick/20"
                placeholder="e.g., 134.22"
                value={amount}
                onChange={event => setAmount(event.target.value)}
                inputMode="decimal"
              />
            </label>
            <button type="submit" className="btn w-full disabled:opacity-70" disabled={loading}>
              {loading ? 'Issuing…' : 'Issue Card'}
            </button>
            {error && <p className="text-sm text-brick">{error}</p>}
            <p className="text-xs text-cocoa/60">
              This card will only work for this checkout and automatically disables after use.
            </p>
          </form>
        ) : (
          <div className="space-y-3">
            <div className="rounded-xl bg-shell p-3">
              <div className="text-xs uppercase tracking-wide text-cocoa/60">Card Number</div>
              <div className="font-mono text-lg text-cocoa">{card.pan}</div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div className="rounded-xl bg-shell p-3">
                <div className="text-xs uppercase tracking-wide text-cocoa/60">Exp</div>
                <div className="font-mono text-cocoa">{card.exp_month}/{String(card.exp_year).slice(-2)}</div>
              </div>
              <div className="rounded-xl bg-shell p-3">
                <div className="text-xs uppercase tracking-wide text-cocoa/60">CVC</div>
                <div className="font-mono text-cocoa">{card.cvc}</div>
              </div>
              <div className="rounded-xl bg-shell p-3">
                <div className="text-xs uppercase tracking-wide text-cocoa/60">Last 4</div>
                <div className="font-mono text-cocoa">{card.last4}</div>
              </div>
            </div>
            <button type="button" className="btn w-full" onClick={handleClose}>
              Done
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
